import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { theme } from '../theme';

const EDITOR_FONT = "'SF Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace";
const MONACO_THEME = 'fusionquery-dark';

const MonacoSQLEditor = forwardRef(function MonacoSQLEditor(
  { modelPath, value, onChange, onRun, onFormat, onSelectionChange, disabled },
  ref
) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  const notifySelection = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !onSelectionChange) return;

    const model = editor.getModel();
    const selection = editor.getSelection();
    if (!model || !selection) return;

    const start = model.getOffsetAt(selection.getStartPosition());
    const end = model.getOffsetAt(selection.getEndPosition());
    const selected = model.getValueInRange(selection);
    onSelectionChange({ text: selected.trim(), start, end });
  }, [onSelectionChange]);

  const runSelectedOrAll = useCallback(() => {
    if (disabled) return;
    const editor = editorRef.current;
    if (!editor) return;

    const model = editor.getModel();
    const selection = editor.getSelection();
    if (!model || !selection) return;

    const selected = model.getValueInRange(selection).trim();
    onRun(selected || null);
  }, [disabled, onRun]);

  const defineFusionQueryTheme = useCallback((monaco) => {
    monaco.editor.defineTheme(MONACO_THEME, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword.sql', foreground: '5ec4c0' },
        { token: 'number.sql', foreground: 'd19a66' },
        { token: 'string.sql', foreground: '98c379' },
        { token: 'comment.sql', foreground: '6b7280', fontStyle: 'italic' },
      ],
      colors: {
        'editor.background': theme.codeBg,
        'editor.foreground': theme.text,
        'editorGutter.background': theme.codeBg,
        'editorLineNumber.foreground': theme.textMuted,
        'editorLineNumber.activeForeground': theme.text,
        'editorLineHighlightBackground': `${theme.surfaceHover}66`,
        'editorCursor.foreground': theme.text,
        'editor.selectionBackground': `${theme.accent}55`,
        'editor.inactiveSelectionBackground': `${theme.accent}33`,
        'editorIndentGuide.background1': `${theme.border}99`,
        'editorIndentGuide.activeBackground1': `${theme.accent}88`,
        'editorWidget.background': theme.surface,
        'editorWidget.border': theme.border,
        'editorSuggestWidget.background': theme.surface,
        'editorSuggestWidget.border': theme.border,
        'editorSuggestWidget.selectedBackground': theme.surfaceHover,
        'scrollbarSlider.background': `${theme.border}99`,
        'scrollbarSlider.hoverBackground': theme.textMuted,
        'scrollbarSlider.activeBackground': theme.textMuted,
      },
    });
  }, []);

  const insertIndentedNewLine = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const model = editor.getModel();
    const selection = editor.getSelection();
    const position = editor.getPosition();
    if (!model || !selection || !position) return;

    const lineNumber = selection.startLineNumber || position.lineNumber;
    const line = model.getLineContent(lineNumber);
    const indent = line.match(/^\s*/)?.[0] || '';
    const nextLineNumber = lineNumber + 1;
    const nextColumn = indent.length + 1;

    editor.pushUndoStop();
    editor.executeEdits('fusionquery.enter', [{
      range: selection,
      text: `\n${indent}`,
      forceMoveMarkers: true,
    }]);
    editor.setPosition({ lineNumber: nextLineNumber, column: nextColumn });
    editor.pushUndoStop();
  }, []);

  useImperativeHandle(ref, () => ({
    restoreSelection(start, end) {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco) return;

      const model = editor.getModel();
      if (!model) return;

      const startPos = model.getPositionAt(start);
      const endPos = model.getPositionAt(end);
      const range = new monaco.Range(
        startPos.lineNumber,
        startPos.column,
        endPos.lineNumber,
        endPos.column
      );
      editor.focus();
      editor.setSelection(range);
      editor.revealLineInCenter(startPos.lineNumber);
    },
    clearSelection() {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco) return;
      const pos = editor.getPosition();
      if (!pos) return;
      const range = new monaco.Range(
        pos.lineNumber,
        pos.column,
        pos.lineNumber,
        pos.column
      );
      editor.setSelection(range);
    },
  }));

  const handleMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    defineFusionQueryTheme(monaco);
    monaco.editor.setTheme(MONACO_THEME);

    // Keep existing shortcut behavior for query execution.
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      runSelectedOrAll();
    });

    // Keep existing shortcut behavior for line comments.
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Slash, () => {
      editor.trigger('keyboard', 'editor.action.commentLine', null);
    });

    // Shared format command (same handler as toolbar).
    const formatKeybinding = monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF;
    editor.addCommand(formatKeybinding, () => {
      onFormat?.();
    });

    const editorDomNode = editor.getDomNode();
    const handleDomKeyDown = (event) => {
      const isPlainEnter = event.key === 'Enter'
        && !event.ctrlKey
        && !event.metaKey
        && !event.altKey
        && !event.shiftKey;
      if (!isPlainEnter) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      insertIndentedNewLine();
    };

    editorDomNode?.addEventListener('keydown', handleDomKeyDown, true);
    editor.onDidDispose(() => {
      editorDomNode?.removeEventListener('keydown', handleDomKeyDown, true);
    });

    // Fallback for browser/layout edge cases where keybinding resolution can miss.
    editor.onKeyDown((e) => {
      if (e.equals(formatKeybinding)) {
        e.preventDefault();
        e.stopPropagation();
        onFormat?.();
      }
    });

    editor.onDidChangeCursorSelection(() => notifySelection());
  }, [defineFusionQueryTheme, insertIndentedNewLine, notifySelection, onFormat, runSelectedOrAll]);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
      background: theme.codeBg,
    }}>
      <Editor
        path={modelPath || 'tab.sql'}
        language="sql"
        theme={MONACO_THEME}
        value={value}
        onChange={(nextValue) => onChange(nextValue || '')}
        beforeMount={defineFusionQueryTheme}
        onMount={handleMount}
        options={{
          // Keep legacy behavior parity: editing is always allowed.
          // Query execution remains gated elsewhere when no connection exists.
          readOnly: false,
          minimap: { enabled: false },
          automaticLayout: true,
          fontFamily: EDITOR_FONT,
          fontSize: 14,
          lineHeight: 22,
          tabSize: 2,
          insertSpaces: true,
          autoIndent: 'full',
          wordWrap: 'off',
          scrollBeyondLastLine: false,
          renderLineHighlight: 'line',
          bracketPairColorization: { enabled: true },
          guides: { indentation: true },
        }}
      />
    </div>
  );
});

export default MonacoSQLEditor;
